import React from 'react';
import type { RacingSessionStatus, QualityThresholds } from './types';
import {
  StatusIcon, getStatusLabel, getIceStateLabel, connTypeView, evaluatePass, lossPctOf,
} from './helpers';

interface SessionRowProps {
  label: string;
  client?: RacingSessionStatus;
  host?: RacingSessionStatus;
  thresholds: QualityThresholds;
  raceCompleted?: boolean;  // race 已选出 winner（回看/完成）：状态显示"已选中/未选中"
}

// 判定徽标
const PassBadge: React.FC<{ pass: boolean | null; isWinner?: boolean }> = ({ pass, isWinner }) => {
  if (pass === null) {
    return isWinner ? <span className="text-emerald-400 text-[10px]">🏆</span> : <span className="text-zinc-600 text-[10px]">—</span>;
  }
  return (
    <span className={`text-[9px] font-bold ${pass ? 'text-emerald-400' : 'text-red-400'}`}>
      {pass ? '✓PASS' : '✗FAIL'}{isWinner ? ' 🏆' : ''}
    </span>
  );
};

const SessionRow: React.FC<SessionRowProps> = ({ label, client, host, thresholds, raceCompleted }) => {
  const q = client?.quality;
  const isTesting = client?.status === 'testing';
  const isWinner = client?.isWinner || host?.isWinner;
  const pass = isTesting ? evaluatePass(q, thresholds) : null;
  const conn = connTypeView(client?.connectionType);
  // race 已完成时：胜出显示"胜出"、其它已完成显示"完成"，不再显示快照定格的"测试中"
  const statusLabel = raceCompleted && client
    ? (isWinner ? '胜出' : '完成')
    : (client ? getStatusLabel(client.status) : '');
  // 每 session 双侧 TURN：主机名简化（rtc3.03kan.com → rtc3），不一致则警示双跳
  const turnShort = (s?: string) => s ? s.split('.')[0] : '';
  const clientTurn = turnShort(client?.turnServer);
  const hostTurn = turnShort(host?.turnServer);
  const turnMismatch = !!clientTurn && !!hostTurn && clientTurn !== hostTurn;
  // host 走内网 forwarder 时：显示本 session 自己的 "内网入口:端口->公网出口"（如 192.168.11.119:13460->rtc）
  // 入口取该 session 实际选中的 actualTurnEntry（host 按 session 上报），不再复用全局 winner 值
  const hostEntry = host?.actualTurnEntry;
  const isPrivateIp = (s?: string) => !!s && /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(s.split(':')[0]);
  const hostDisplay = (isPrivateIp(hostEntry) && hostTurn)
    ? `${hostEntry}->${hostTurn}`
    : (hostTurn || '?');

  return (
    <div className={`rounded px-2 py-1 ${
      isWinner ? 'bg-emerald-500/20 border border-emerald-500/30'
      : raceCompleted ? 'bg-emerald-500/10 border border-emerald-500/15'
      : isTesting ? 'bg-cyan-500/10 border border-cyan-500/20'
      : 'bg-white/3'
    }`}>
      {/* 主行（Client 视角） */}
      <div className="flex items-center gap-1.5 text-[10px] min-w-0">
        {client && <StatusIcon status={client.status} isWinner={isWinner} />}
        <span className={`w-[88px] truncate font-medium ${isWinner ? 'text-emerald-300' : 'text-zinc-300'}`}>{label}</span>

        {client ? (
          <span className={raceCompleted ? (isWinner ? 'text-emerald-300 font-bold' : 'text-emerald-400/70') : 'text-zinc-400'}>{statusLabel}</span>
        ) : (
          <span className="text-zinc-600">-</span>
        )}

        {client?.connectionType && (
          <span className={`text-[8px] px-1 rounded ${conn.cls}`}>{conn.label}</span>
        )}

        {client?.iceState && (client.status === 'racing' || client.status === 'offer_received') && (
          <span className="text-zinc-500 text-[9px]">{getIceStateLabel(client.iceState)}</span>
        )}

        {/* 质量指标（测试中或已有数据时显示） */}
        {q && q.packetsReceived > 0 && (
          <span className="ml-auto font-mono text-cyan-400 text-[9px] flex items-center gap-1.5">
            <span title="RTT">{Math.round(q.rtt ?? 0)}ms</span>
            <span title="RTT 抖动">jit{Math.round(q.rttJitterMs ?? 0)}</span>
            <span title="丢包率">{lossPctOf(q).toFixed(1)}%</span>
            <span title="吞吐">{q.throughputKbps}k</span>
            <span title="吞吐稳定性 min/mean">{q.stableThroughputKbps ?? 0}%</span>
            {(q.score ?? 0) > 0 && <span className="text-yellow-400" title="评分">[{q.score}]</span>}
          </span>
        )}

        <span className="ml-1.5 flex-shrink-0"><PassBadge pass={pass} isWinner={isWinner} /></span>
      </div>

      {/* Host 折叠副行 */}
      <div className="flex items-center gap-1 text-[9px] text-zinc-500 pl-[18px] mt-0.5">
        <span className="text-zinc-600">└Host</span>
        {host?.quality ? (
          <span className="font-mono">已发{host.quality.packetsSent ?? 0}包 / {host.quality.throughputKbps}k</span>
        ) : (
          <span className="text-zinc-600">-</span>
        )}
      </div>

      {/* 每 session 双侧 TURN（浏览器实际 Allocate / host 实际 Allocate）*/}
      {(clientTurn || hostTurn) && (
        <div className="flex items-center gap-1 text-[9px] pl-[18px] mt-0.5 font-mono">
          <span className="text-zinc-600">└TURN</span>
          <span className="text-zinc-400">浏览器 {clientTurn || '?'}</span>
          <span className="text-zinc-600">·</span>
          <span className={turnMismatch ? 'text-amber-400 font-bold' : 'text-zinc-400'}>主机 {hostDisplay}</span>
          {turnMismatch && <span className="text-amber-400" title="双方 TURN 不一致，可能双跳">⚠</span>}
        </div>
      )}
    </div>
  );
};

export default SessionRow;
